import type { SurfObservation } from 'types/surf';

/** Live observations can land slightly off the forecast hour; match SurfTooltip's tolerance. */
export const SURF_OBSERVATION_TIMESTAMP_TOLERANCE_SECONDS = 300;

export const isSurfObservationTimestampMatch = (
  observationTimestamp: number,
  forecastTimestamp: number,
) =>
  observationTimestamp === forecastTimestamp ||
  Math.abs(observationTimestamp - forecastTimestamp) < SURF_OBSERVATION_TIMESTAMP_TOLERANCE_SECONDS;

/**
 * Finds the observation for a forecast timestamp: exact match first, then within 5 minutes.
 * Mirrors SurfTooltip so graph surfaces and tooltips stay consistent.
 */
export const findMatchingSurfObservation = (
  observations: SurfObservation[] | undefined,
  forecastTimestamp: number | undefined,
): SurfObservation | null => {
  if (forecastTimestamp == null || !observations?.length) return null;

  const exactMatch = observations.find(
    (observation) => observation.timestamp === forecastTimestamp && observation.surf !== null,
  );
  if (exactMatch) return exactMatch;

  return (
    observations.find(
      (observation) =>
        isSurfObservationTimestampMatch(observation.timestamp, forecastTimestamp) &&
        observation.surf !== null,
    ) ?? null
  );
};

/**
 * Maps an observation onto a forecast hour index using the same timestamp tolerance.
 */
export const findMatchingForecastHourIndex = <T extends { timestamp: number }>(
  forecasts: T[],
  observationTimestamp: number,
): number | undefined => {
  const exactIndex = forecasts.findIndex((forecast) => forecast.timestamp === observationTimestamp);
  if (exactIndex !== -1) return exactIndex;

  const closeIndex = forecasts.findIndex((forecast) =>
    isSurfObservationTimestampMatch(observationTimestamp, forecast.timestamp),
  );
  return closeIndex === -1 ? undefined : closeIndex;
};

export const doesObservationMatchForecastTimestamps = (
  observationTimestamp: number,
  forecastTimestamps: Iterable<number>,
) => {
  for (const forecastTimestamp of forecastTimestamps) {
    if (isSurfObservationTimestampMatch(observationTimestamp, forecastTimestamp)) {
      return true;
    }
  }
  return false;
};
